// @ts-nocheck
import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  TextField,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { DateUtils, StringUtils } from '../../../../common/utils';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { ProductChangeLine, ProductChangeLineBuilder } from './models/productChangeLine';
import { ProductChange, ProductChangeBuilder } from './models/productChange';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '64px',
      '& .MuiFormHelperText-marginDense': {
        marginTop: '2px',
      },
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    marginBottom: theme.spacing(1),
    overflow: 'auto',
  },
  formSection: {
    flex: 1,
    '&:first-child': {
      marginRight: theme.spacing(3),
    },
    '&:last-child': {
      flex: '1 1 100%',
    },
    '& .MuiTypography-gutterBottom': {
      marginBottom: '1em',
    },
    '& .MuiGrid-item': {
      padding: theme.spacing(0, 1),
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function ProductChangeForm(props) {
  const {
    product,
    productChange,
    onSave,
    onError,
    onCancel,
    editMode,
    actionsDisabled,
  } = props;
  
  const [editable, setEditable] = useState(editMode);
  const [t] = useTranslation();
  const { formState, errors, control, register, handleSubmit } = useForm();
  const classes = useStyles();
  const change: ProductChange = productChange;

  const { isDirty, isSubmitting } = formState;

  const compareValues: <T>(newValue: T, oldValue: T) => boolean = (newValue, oldValue) => {
    return newValue !== oldValue;
  }

  const getProductChangeProperty = (propName: string) => {
    if (change) {
      return change.productChangeLines.find(line => line.type === propName)?.value;
    }

    return undefined;
  };

  const getProductChangeLine: (value: string, type: string | number | boolean) => ProductChangeLine = (value, type) => {
    return new ProductChangeLineBuilder()
      .withType(type)
      .withValue(value)
      .build();
  }

  const onSubmit = async data => {
    const newProductChange = new ProductChangeBuilder()
    .withProductId(product.id)
    .withEffectiveDate(data.effectiveDate);

    if(compareValues(data.title, product.title))
      newProductChange.withProductChangeLine(getProductChangeLine(data.title, "TITLE"));

    if(compareValues(data.description, product.description))
      newProductChange.withProductChangeLine(getProductChangeLine(data.description, "DESCRIPTION"));
    
    const price =  _.toNumber(data.price.replace(',', '')) ?? 0;
    if(compareValues(price, product.price))
      newProductChange.withProductChangeLine(getProductChangeLine(price, "PRICE"));

    if(compareValues(data.suspendFree, product.suspendFree))
      newProductChange.withProductChangeLine(getProductChangeLine(data.suspendFree, "SUSPENDFREE"));

    const freePeriod =  _.toNumber(data.freePeriod) ?? 0;
    if(compareValues(freePeriod, product.discountDays))
      newProductChange.withProductChangeLine(getProductChangeLine(freePeriod, "FREEPERIOD"));

    const commitmentDays =  _.toNumber(data.commitmentDays) ?? 0;
    if(compareValues(commitmentDays, product.commitmentDays))
      newProductChange.withProductChangeLine(getProductChangeLine(commitmentDays, "COMMITMENTDAYS"));

    const quarantineDays =  _.toNumber(data.quarantineDays) ?? 0;
    if(compareValues(quarantineDays, product.discountEligibilityIntervalDays))
      newProductChange.withProductChangeLine(getProductChangeLine(quarantineDays, "QUARANTINEDAYS"));

    const formData = omitEmpty(newProductChange.build());
    
    if(!formData.productChangeLines) {
      console.log(formData);
      onError(t('product.productChangeRequired'));
      return;
    }

    try {
      await onSave(formData);
      ifMounted(() => {
        reset(data);
        if (editMode) {
          setEditable(false);
        }
      });
    } catch (e) {
      // Error already handled
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const validateEffectiveDate = value => {
    const today = new Date();
    if (!DateUtils.isValid(value)) {
      return t('product.productChangeEffectiveDateInvalid');
    }
    if (DateUtils.compareDates(value, today) < 0) {
      return t('product.productChangeEffectiveDateBeforeStartDate');
    }
    return true;
  };

  const handleEffectiveDateChange = (params, value) => {
    params.onChange(_.defaultTo(value?.format('YYYY-MM-DDTHH:mm:ssZ'), ''));
  };

  return (
    <Box id="ProductChangeForm-container" className={classes.container}>
      <form id="ProductChangeForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <TextField
                id="ProductChangeForm-id"
                name="id"
                label={t('product.id')}
                variant="outlined"
                size="small"
                defaultValue={product?.id}
                inputRef={register}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="ProductChangeForm-title"
                name="title"
                label={t('product.productTitle')}
                variant="outlined"
                size="small"
                defaultValue={_.defaultTo(getProductChangeProperty("TITLE"), product?.title)}
                inputRef={register({
                  required: t('product.productTitleRequired'),
                  pattern: {
                    value: /^\S.{0,}$/,
                    message: t('product.productTitleRequired'),
                  },
                  validate: value => value.trim() !== ''
                })}
                error={!!errors.title}
                helperText={errors.title?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="ProductChangeForm-price"
                name="price"
                label={t('product.productPrice')}
                variant="outlined"
                size="small"
                defaultValue={StringUtils.getFormattedDecimal(_.defaultTo(getProductChangeProperty("PRICE"), product?.price))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">Kr.</InputAdornment>,
                }}
                inputRef={register({
                  required: t('product.productPriceRequired'),
                  pattern: {
                    value: /^[0-9]*[0-9][,][0-9]{2}$/,
                    message: t('product.productChangeValueDecimal'),
                  },
                })}
                error={!!errors.price}
                helperText={errors.price?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="ProductChangeForm-freePeriod"
                name="freePeriod"
                label={t('product.productFreePeriod')}
                variant="outlined"
                size="small"
                defaultValue={_.defaultTo(getProductChangeProperty("FREEPERIOD"), product?.discountDays)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">dage</InputAdornment>,
                }}
                inputRef={register({
                  required: t('product.productFreePeriodRequired'),
                  pattern: {
                    value: /^[0-9]+$/,
                    message: t('campaign.campaignValidDaysInteger'),
                  },
                })}
                error={!!errors.freePeriod}
                helperText={errors.freePeriod?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="ProductChangeForm-commitmentDays"
                name="commitmentDays"
                label={t('product.productCommitmentDays')}
                variant="outlined"
                size="small"
                defaultValue={_.defaultTo(getProductChangeProperty("COMMITMENTDAYS"), product?.commitmentDays)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">dage</InputAdornment>,
                }}
                inputRef={register({
                  required: t('product.productCommitmentDaysRequired'),
                  pattern: {
                    value: /^[0-9]+$/,
                    message: t('campaign.campaignValidDaysInteger'),
                  },
                })}
                error={!!errors.commitmentDays}
                helperText={errors.commitmentDays?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="ProductChangeForm-quarantineDays"
                name="quarantineDays"
                label={t('product.productQuarantineDays')}
                variant="outlined"
                size="small"
                defaultValue={_.defaultTo(getProductChangeProperty("QUARANTINEDAYS"), product?.discountEligibilityIntervalDays)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">dage</InputAdornment>,
                }}
                inputRef={register({
                  required: t('product.productQuarantineDaysRequired'),
                  pattern: {
                    value: /^[0-9]+$/,
                    message: t('campaign.campaignValidDaysInteger'),
                  },
                })}
                error={!!errors.quarantineDays}
                helperText={errors.quarantineDays?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={8}>
              <Controller
                name="effectiveDate"
                control={control}
                defaultValue={_.defaultTo(
                  change
                    ? DateUtils.format(
                        change.effectiveDate.replace(/(\[.+\])/g, ''),
                        'YYYY-MM-DDTHH:mm:ssZ'
                      )
                    : null,
                  DateUtils.format(DateUtils.daysLater(1, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ'))
                )}
                rules={{
                  validate: validateEffectiveDate,
                }}
                render={params => (
                  <KeyboardDatePicker
                    id="ProductChangeForm-effectiveDate"
                    label={t('product.productChangeEffectiveDate')}
                    variant="inline"
                    inputVariant="outlined"
                    format="YYYY-MM-DD HH:mm:ss"
                    value={params.value}
                    onChange={value => handleEffectiveDateChange(params, value)}
                    error={!!errors.effectiveDate}
                    helperText={errors.effectiveDate?.message}
                    autoOk
                    fullWidth
                    disabled={!editable}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                label={t('product.productSuspendFree')}
                labelPlacement="end"
                className={classes.switch}
                control={
                  <Controller
                    name="suspendFree"
                    control={control}
                    defaultValue={_.defaultTo(getProductChangeProperty("SUSPENDFREE"), product?.suspendFree ?? false)}
                    render={params => (
                      <Switch
                        id="ProductChangeForm-suspendFree"
                        checked={params.value}
                        onChange={e => params.onChange(e.target.checked)}
                        disabled={!editable}
                      />
                    )}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ProductChangeForm-description"
                name="description"
                label={t('product.productDescription')}
                variant="outlined"
                size="small"
                defaultValue={_.defaultTo(getProductChangeProperty("DESCRIPTION"), product?.description)}
                inputRef={register}
                disabled={!editable}
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="ProductChangeForm-cancel"
            className={classes.button}
            startIcon={<Icon>{!editable ? 'chevron_left' : 'cancel'}</Icon>}
            onClick={handleCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {!editable ? t('common.back') : t('common.cancel')}
            </Typography>
          </Button>
          { editable &&
            <Button
              id="ProductChangeForm-save"
              key="save"
              type="submit"
              color="primary"
              className={classes.button}
              startIcon={<Icon>save</Icon>}
              disabled={actionsDisabled || isSubmitting || !isDirty}
            >
              <Typography variant="button" noWrap>
                {t('common.create')}
              </Typography>
            </Button>
          }
        </Box>
      </form>
    </Box>
  );
}

ProductChangeForm.defaultProps = {
  product: undefined,
  productChange: undefined,
  cancelButtonText: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

ProductChangeForm.propTypes = {
  product: PropTypes.object,
  productChange: PropTypes.shape({
    id: PropTypes.number,
    clientId: PropTypes.number,
    productId: PropTypes.number,
    state: PropTypes.string,
    createdAt: PropTypes.string,
    effectiveDate: PropTypes.string,
    creator: PropTypes.string,
    productChangeLines: PropTypes.arrayOf(
      PropTypes.shape({
        changeId: PropTypes.number,
        productChangeId: PropTypes.number,
        group: PropTypes.string,
        type: PropTypes.string,
        value: PropTypes.string
      })
    )
  }),
  cancelButtonText: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
};

export default ProductChangeForm;
